import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  CreateDocFeedbackRequest,
  DocFeedback,
  DocFeedbackEntitySummary,
  DocFeedbackList,
  DocFeedbackListRequest,
  DocPageRatingList,
  DocPageRatingsRequest,
  DocRatingRequest,
  DocRatingSummary,
  UpdateDocFeedbackRequest,
} from '@internal/backstage-plugin-doc-feedback-common';

/**
 * Client for the doc-feedback backend.
 *
 * @public
 */
export interface DocFeedbackApi {
  submitFeedback(request: CreateDocFeedbackRequest): Promise<DocFeedback>;
  listFeedback(request?: DocFeedbackListRequest): Promise<DocFeedbackList>;
  getFeedback(id: string): Promise<DocFeedback>;
  updateFeedback(id: string, request: UpdateDocFeedbackRequest): Promise<DocFeedback>;
  rate(request: DocRatingRequest): Promise<DocRatingSummary>;
  getRatingSummary(options: { entityRef: string; pagePath?: string }): Promise<DocRatingSummary>;
  getEntitySummary(entityRef: string): Promise<DocFeedbackEntitySummary>;
  /** Per-page "Was this page helpful?" results. */
  listPageRatings(request?: DocPageRatingsRequest): Promise<DocPageRatingList>;
}

/** @public */
export const docFeedbackApiRef = createApiRef<DocFeedbackApi>({
  id: 'plugin.doc-feedback.service',
});

/** @public */
export class DocFeedbackClient implements DocFeedbackApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async request<T>(path: string, init?: { method: string; body: unknown }): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('doc-feedback');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: init?.method ?? 'GET',
      headers: init ? { 'Content-Type': 'application/json' } : undefined,
      body: init ? JSON.stringify(init.body) : undefined,
    });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return (await response.json()) as T;
  }

  submitFeedback(request: CreateDocFeedbackRequest): Promise<DocFeedback> {
    return this.request('/feedback', { method: 'POST', body: request });
  }

  listFeedback(request: DocFeedbackListRequest = {}): Promise<DocFeedbackList> {
    const params = new URLSearchParams();
    if (request.scope) params.set('scope', request.scope);
    if (request.status?.length) params.set('status', request.status.join(','));
    if (request.category?.length) {
      params.set('category', request.category.join(','));
    }
    if (request.entityRef) params.set('entityRef', request.entityRef);
    if (request.ownerRef) params.set('ownerRef', request.ownerRef);
    if (request.limit !== undefined) params.set('limit', String(request.limit));
    if (request.offset) params.set('offset', String(request.offset));
    const query = params.toString();
    return this.request(`/feedback${query ? `?${query}` : ''}`);
  }

  getFeedback(id: string): Promise<DocFeedback> {
    return this.request(`/feedback/${encodeURIComponent(id)}`);
  }

  updateFeedback(id: string, request: UpdateDocFeedbackRequest): Promise<DocFeedback> {
    return this.request(`/feedback/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: request,
    });
  }

  rate(request: DocRatingRequest): Promise<DocRatingSummary> {
    return this.request('/ratings', { method: 'POST', body: request });
  }

  getRatingSummary(options: { entityRef: string; pagePath?: string }): Promise<DocRatingSummary> {
    const params = new URLSearchParams({ entityRef: options.entityRef });
    if (options.pagePath !== undefined) {
      params.set('pagePath', options.pagePath);
    }
    return this.request(`/ratings/summary?${params}`);
  }

  getEntitySummary(entityRef: string): Promise<DocFeedbackEntitySummary> {
    return this.request(`/summary?${new URLSearchParams({ entityRef }).toString()}`);
  }

  listPageRatings(request: DocPageRatingsRequest = {}): Promise<DocPageRatingList> {
    const params = new URLSearchParams();
    if (request.scope) params.set('scope', request.scope);
    if (request.entityRef) params.set('entityRef', request.entityRef);
    if (request.ownerRef) params.set('ownerRef', request.ownerRef);
    const query = params.toString();
    return this.request(`/ratings${query ? `?${query}` : ''}`);
  }
}
